import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { loadFontFace, removeFontFace, stripFontExtension } from '../../../../utils/fontFace';

const releaseFonts = (fonts) => fonts.forEach((font) => removeFontFace(font.face));

const loadPendingFont = async (file) => {
  const id = uuidv4();
  const family = `ms-settings-pending-font-${id}`;
  const face = await loadFontFace(family, await file.arrayBuffer());
  return {
    id,
    file,
    face,
    fileName: file.name,
    label: stripFontExtension(file.name),
    stack: `"${family}", sans-serif`,
  };
};

const usePendingFonts = ({ onError, onSave }) => {
  const [pendingFonts, setPendingFonts] = useState([]);
  const pendingFontsRef = useRef(pendingFonts);
  pendingFontsRef.current = pendingFonts;

  useEffect(() => () => releaseFonts(pendingFontsRef.current), []);

  const clearFonts = useCallback(() => {
    releaseFonts(pendingFontsRef.current);
    pendingFontsRef.current = [];
    setPendingFonts([]);
  }, []);

  const addFiles = useCallback(
    async (files) => {
      const results = await Promise.allSettled(files.map(loadPendingFont));
      const fonts = results.filter((result) => result.status === 'fulfilled').map(({ value }) => value);

      if (fonts.length < files.length) {
        onError();
      }
      setPendingFonts((current) => [...current, ...fonts]);
    },
    [onError],
  );

  const renameFont = useCallback((id, label) => {
    setPendingFonts((current) => current.map((font) => (font.id === id ? { ...font, label } : font)));
  }, []);

  const saveFonts = useCallback(() => {
    onSave(pendingFontsRef.current.map(({ file, label }) => ({ file, label: label.trim() })));
    clearFonts();
  }, [onSave, clearFonts]);

  return { pendingFonts, addFiles, renameFont, saveFonts, discardFonts: clearFonts };
};

export default usePendingFonts;
